import type { QueryResult } from '@apollo/client/react';
import type { ListVariantTypeAndProps } from '@aurora/shared-client/components/common/List';
import { PagerVariant } from '@aurora/shared-client/components/common/Pager/enums';
import { PanelType } from '@aurora/shared-client/components/common/Panel/enums';
import type { IdeaStatusDataConnection } from '@aurora/shared-generated/types/graphql-schema-types';
import type { EndUserPages, EndUserQueryParams } from '@aurora/shared-types/pages/enums';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import React, { useEffect, useState } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import { ItemType, IdeaStatusViewVariant } from '../../../../types/enums';
import type {
  FeaturedIdeaStatusesWidgetQuery,
  FeaturedIdeaStatusesWidgetQueryVariables
} from '../../../../types/graphql-types';
import PaneledItemList from '../../../common/List/PaneledItemList/PaneledItemList';
import type { ItemViewTypeAndProps } from '../../../entities/types';
import useTranslation from '../../../useTranslation';
import FeaturedWidgetPlaceholder from '../../FeaturedWidgetPlaceholder/FeaturedWidgetPlaceholder';
import useFeaturedIdeaStatusesWidget from '../FeaturedIdeaStatusesWidget/useFeaturedIdeaStatusesWidget';
import type { IdeaStatusDetailsFragment } from '@aurora/shared-generated/types/graphql-types';
import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import { IconColor, IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import Icon from '@aurora/shared-client/components/common/Icon/Icon';
import Icons from '@aurora/shared-client/icons';
import localStyles from './FeaturedIdeaStatusesList.module.pcss';
import type { PagerVariantTypeAndProps } from '@aurora/shared-client/components/common/Pager/types';

interface Props {
  /**
   * Variables for the FeaturedIdeaStatusesWidget query
   */
  ideaStatusesQueryVariables: FeaturedIdeaStatusesWidgetQueryVariables;
  /**
   * Callback function used to lift the query result up to the parent widget
   */
  onIdeaStatusesQueryUpdate: (
    queryResult: QueryResult<
      FeaturedIdeaStatusesWidgetQuery,
      FeaturedIdeaStatusesWidgetQueryVariables
    >
  ) => void;
  /**
   * Callback function that toggles the visibility of the AddFeaturedIdeaStatusesModal
   */
  toggleModal: () => void;
  /**
   * The configuration for the PaneledItemList header
   */
  header: { title: string; titleSrOnly: boolean };
  /**
   * View variant type and props for the idea statuses
   */
  viewVariant: ItemViewTypeAndProps<
    ItemType.IDEA_STATUS,
    IdeaStatusViewVariant.INLINE | IdeaStatusViewVariant.CARD
  >;
  /**
   * The variant display style used when rendering the list of statuses
   */
  listVariant: ListVariantTypeAndProps<IdeaStatusDetailsFragment>;
  /**
   * Applies panel styling to the component
   */
  panelType: PanelType;
  /**
   * The style of pager to use
   */
  pagerVariant: PagerVariantTypeAndProps<EndUserPages, EndUserQueryParams>;
  /**
   * Whether to display the pager or not
   */
  showPager: boolean;
  /**
   * Class name(s) to apply to the featured ideaStatuses node list
   */
  className: string;
  /**
   * The number of items to display
   */
  pageSize?: number;
}

/**
 * Renders the list of featured ideaStatuses nodes
 *
 * @author rishabh.jivan
 */
const FeaturedIdeaStatusesList: React.FC<React.PropsWithChildren<Props>> = ({
  ideaStatusesQueryVariables,
  onIdeaStatusesQueryUpdate,
  toggleModal,
  header,
  viewVariant,
  listVariant,
  panelType,
  pagerVariant,
  showPager,
  className,
  pageSize
}) => {
  const cx = useClassNameMapper(localStyles);
  const i18n = useTranslation(EndUserComponent.FEATURED_IDEA_STATUSES_LIST);
  const { formatMessage, loading: textLoading } = i18n;

  const ideaStatusesQueryResult: QueryResult<
    FeaturedIdeaStatusesWidgetQuery,
    FeaturedIdeaStatusesWidgetQueryVariables
  > = useFeaturedIdeaStatusesWidget(ideaStatusesQueryVariables);

  const [statusesPageSize, setStatusesPageSize] = useState<number>(pageSize);

  useEffect(() => {
    setStatusesPageSize(pageSize);
  }, [pageSize]);

  useEffect(() => {
    onIdeaStatusesQueryUpdate(ideaStatusesQueryResult);
  }, [ideaStatusesQueryResult, onIdeaStatusesQueryUpdate]);

  if (textLoading || ideaStatusesQueryResult.loading) {
    return null;
  }

  const featuredStatuses = ideaStatusesQueryResult?.data?.featuredStatusesWidget?.statuses?.edges;
  const containsIdeaStatuses: boolean = featuredStatuses?.length > 0;
  const showFooter = showPager && featuredStatuses?.length > statusesPageSize;
  const paginatedQueryResult = containsIdeaStatuses
    ? {
        ...ideaStatusesQueryResult,
        data: {
          ...ideaStatusesQueryResult.data,
          featuredStatusesWidget: {
            ...ideaStatusesQueryResult.data.featuredStatusesWidget,
            statuses: {
              ...ideaStatusesQueryResult.data.featuredStatusesWidget.statuses,
              edges: featuredStatuses.slice(0, statusesPageSize)
            }
          }
        }
      }
    : ideaStatusesQueryResult;

  /**
   * Renders the empty state for the FeaturedIdeaStatusesWidget
   */
  const EmptyState: React.FC<React.PropsWithChildren<unknown>> = () => (
    <FeaturedWidgetPlaceholder buttonText={formatMessage('edit')} onClick={toggleModal} />
  );

  const { title, titleSrOnly } = header;

  /**
   * Function to render the show more button
   */
  function renderFooter(): React.ReactElement {
    return (
      showFooter &&
      pagerVariant.type === PagerVariant.LOAD_MORE && (
        <Button
          onClick={() => setStatusesPageSize(statusesPageSize + pageSize)}
          variant={ButtonVariant.LINK}
          className={cx('lia-g-loader-btn')}
          data-testid="IdeaStatusList.showMore"
        >
          <Icon
            icon={Icons.ChevronDownIcon}
            color={IconColor.LOAD_TEXT}
            size={IconSize.PX_16}
            className={cx('lia-g-mr-5')}
          />
          {formatMessage('showMoreLink')}
        </Button>
      )
    );
  }

  /**
   * Renders the header for the list.
   */
  function renderHeader(): React.ReactElement {
    return containsIdeaStatuses && <h3 className={cx('text-break h5 mb-0')}>{title}</h3>;
  }

  return (
    <>
      <PaneledItemList<
        IdeaStatusDetailsFragment,
        ItemType.IDEA_STATUS,
        ItemViewTypeAndProps<ItemType.IDEA_STATUS, IdeaStatusViewVariant>,
        FeaturedIdeaStatusesWidgetQuery,
        FeaturedIdeaStatusesWidgetQueryVariables
      >
        id="FeaturedIdeaStatuses"
        queryResult={paginatedQueryResult}
        itemPath="featuredStatusesWidget.statuses"
        onUpdate={(
          ideaStatusDataConnection: IdeaStatusDataConnection
        ): FeaturedIdeaStatusesWidgetQuery => {
          return {
            featuredStatusesWidget: {
              statuses: ideaStatusDataConnection
            }
          };
        }}
        pageSize={pageSize}
        type={ItemType.IDEA_STATUS}
        variant={viewVariant}
        useEmpty
        empty={EmptyState}
        panel={containsIdeaStatuses ? panelType : PanelType.NONE}
        listVariant={listVariant}
        header={renderHeader}
        className={cx(className, { 'lia-list-has-footer': showFooter })}
        isHeaderSrOnly={titleSrOnly}
        useFooter={false}
      />
      {showFooter && (
        <footer
          className={cx(`lia-panel-list-footer-${panelType} justify-content-center d-flex`, {
            'bg-transparent lia-g-mt-5': viewVariant.type === IdeaStatusViewVariant.CARD
          })}
        >
          {renderFooter()}
        </footer>
      )}
    </>
  );
};
export default FeaturedIdeaStatusesList;
